import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Building2 } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { TextField } from "../shared/FormField";
import { apiClient, getApiErrorMessage } from "../../lib/apiClient";

const EMPTY_FORM = { name: "", address: "", city: "", state: "", postal_code: "", phone: "" };

export function OutletInfoCard({ onOutletLoaded }) {
  const [outlet, setOutlet] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiClient.get("/outlet/settings")
      .then(({ data }) => {
        setOutlet(data);
        setForm({
          name: data.name || "", address: data.address || "", city: data.city || "",
          state: data.state || "", postal_code: data.postal_code || "", phone: data.phone || "",
        });
        onOutletLoaded?.(data);
      })
      .catch((error) => toast.error(getApiErrorMessage(error, "Could not load outlet settings.")))
      .finally(() => setLoading(false));
  }, [onOutletLoaded]);

  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSave = async () => {
    if (!outlet) return;
    setSaving(true);
    try {
      const { data } = await apiClient.put(`/outlet/${outlet.id}`, {
        name: form.name, address: form.address, city: form.city, state: form.state,
        postal_code: form.postal_code, phone: form.phone,
        latitude: outlet.latitude, longitude: outlet.longitude, area: outlet.area,
      });
      setOutlet(data);
      onOutletLoaded?.(data);
      toast.success("Outlet info updated.");
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Could not update outlet info."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="settings-outlet-info-card">
      <CardContent className="p-5">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
            <Building2 size={18} strokeWidth={1.5} />
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-heading text-sm font-semibold text-text-main">Outlet Information</p>
            <p className="mt-0.5 text-xs text-text-muted">Your business's name, address and phone — shown across receipts and driver apps.</p>
          </div>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <TextField label="Outlet Name" value={form.name} onChange={setField("name")} disabled={loading} testId="settings-outlet-name" wrapperClassName="sm:col-span-2" />
          <TextField label="Address" value={form.address} onChange={setField("address")} disabled={loading} testId="settings-outlet-address" wrapperClassName="sm:col-span-2" />
          <TextField label="City" value={form.city} onChange={setField("city")} disabled={loading} testId="settings-outlet-city" />
          <TextField label="Province/State" value={form.state} onChange={setField("state")} disabled={loading} testId="settings-outlet-state" />
          <TextField label="Postal Code" value={form.postal_code} onChange={setField("postal_code")} disabled={loading} testId="settings-outlet-postal-code" />
          <TextField label="Phone" value={form.phone} onChange={setField("phone")} disabled={loading} testId="settings-outlet-phone" />
        </div>

        <div className="mt-4 flex justify-end">
          <Button size="sm" onClick={handleSave} disabled={saving || loading} data-testid="settings-outlet-save-button">
            {saving ? "Saving..." : "Save Outlet Info"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
